import React, { RefObject, useEffect, useRef, useState } from 'react';
import {
  List,
  Phone,
  Mail,
  FileText,
  Globe,
  PhoneCall,
  BarChart3,
  CheckCircle2,
} from 'lucide-react';
import './Landing.css';

interface HowItWorksSectionProps {
  visibleSections: Set<string>;
  howItWorksSectionRef: RefObject<HTMLElement>;
}

interface TimelineStep {
  id: number;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

const HowItWorksSection: React.FC<HowItWorksSectionProps> = ({
  visibleSections,
  howItWorksSectionRef,
}) => {
  const [activeSteps, setActiveSteps] = useState<Set<number>>(new Set([0]));
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  const steps: TimelineStep[] = [
    {
      id: 1,
      icon: List,
      title: 'Create Event & Guest List',
      description: 'Set up your event and provide your initial guest list.',
    },
    {
      id: 2,
      icon: Phone,
      title: 'Mirsal Collects Contact Details',
      description:
        'We verify, complete, and organize all guest contact information from our extensive database.',
    },
    {
      id: 3,
      icon: Mail,
      title: 'Automated Invitations Sent',
      description: 'Invitations are automatically distributed via WhatsApp and email to all guests.',
    },
    {
      id: 4,
      icon: FileText,
      title: 'RSVPs Tracked & Reported',
      description:
        'All responses are collected, organized, and reported to you with daily updates and a final report.',
    },
    {
      id: 5,
      icon: Globe,
      title: 'Event Website Created',
      description:
        'A personalized event website is developed featuring the program, guides, and RSVP section.',
    },
    {
      id: 6,
      icon: PhoneCall,
      title: 'Dedicated Phone Line Available',
      description:
        'A dedicated phone line with personalized automated messages handles guest inquiries and RSVPs.',
    },
    {
      id: 7,
      icon: BarChart3,
      title: 'Real-time Updates & Analytics',
      description:
        'Access real-time analytics, response rates, and comprehensive event management insights.',
    },
  ];

  // Scroll-based step activation
  useEffect(() => {
    const section = howItWorksSectionRef.current || sectionRef.current;
    if (!section || !visibleSections.has('how-it-works')) {
      setActiveSteps(new Set([0]));
      setScrollProgress(0);
      return;
    }

    const calculateProgress = () => {
      const rect = section.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Calculate scroll progress (0-100%)
      // Start tracking when section top reaches viewport center
      const viewportCenter = windowHeight / 2;
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      
      // Progress: 0% when section starts entering, 100% when section fully scrolled
      let progress = 0;
      
      if (sectionTop <= viewportCenter && sectionTop + sectionHeight >= viewportCenter) {
        // Section is in view - calculate progress
        // How far has the section scrolled past the viewport center?
        const scrolled = viewportCenter - sectionTop;
        const totalScrollable = sectionHeight;
        progress = Math.min(100, Math.max(0, (scrolled / totalScrollable) * 100));
      } else if (sectionTop + sectionHeight < viewportCenter) {
        // Section has been fully scrolled past
        progress = 100;
      }
      
      setScrollProgress(progress);
      
      // Map progress to active steps
      // Each step gets ~14.28% (100% / 7 steps)
      const stepThreshold = 100 / steps.length;
      const newActiveSteps = new Set<number>();
      
      for (let i = 0; i < steps.length; i++) {
        // Step is active if progress has reached its threshold
        // Add small buffer for smoother activation
        if (progress >= i * stepThreshold - 5) {
          newActiveSteps.add(i);
        }
      }
      
      // Always have at least step 0 active if section is visible
      if (newActiveSteps.size === 0 && progress > 0) {
        newActiveSteps.add(0);
      }
      
      setActiveSteps(newActiveSteps);
    };

    // Initial calculation
    calculateProgress();

    // Throttle scroll events for performance
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          calculateProgress();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', calculateProgress, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', calculateProgress);
    };
  }, [visibleSections, steps.length, howItWorksSectionRef]);

  // Handle ref assignment - React refs can be RefObject (readonly) or MutableRefObject (mutable)
  // Since the prop is typed as RefObject but parent likely uses useRef (MutableRefObject),
  // we need to handle the assignment. The deprecation warning is a TypeScript limitation.
  const setRefs = (el: HTMLElement | null) => {
    sectionRef.current = el;
    
    // Assign to parent's ref if it exists
    // Note: RefObject has readonly current, but in practice parent components
    // using useRef() create MutableRefObject which is assignable
    if (howItWorksSectionRef) {
      // Type assertion needed because TypeScript types RefObject as readonly
      // but runtime behavior allows assignment when it's actually a MutableRefObject
      (howItWorksSectionRef as React.MutableRefObject<HTMLElement | null>).current = el;
    }
  };

  return (
    <section
      id="how-it-works"
      ref={setRefs}
      className="relative z-10 bg-black"
    >
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black pointer-events-none z-0" />

      <div className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Title */}
          <h2
            className={`font-megant text-2xl sm:text-3xl md:text-4xl font-bold mb-8 sm:mb-12 text-center transition-all duration-1000 ${
              visibleSections.has('how-it-works')
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-10'
            }`}
          >
            HOW IT WORKS
          </h2>

          {/* Desktop Timeline - Vertical with alternating sides */}
          <div
            ref={timelineRef}
            className="hidden md:block relative"
          >
            {/* Central Timeline Line */}
            <div
              className={`absolute left-1/2 transform -translate-x-1/2 w-1 bg-gray-800 transition-all duration-2000 ${
                visibleSections.has('how-it-works')
                  ? 'opacity-100 h-full'
                  : 'opacity-0 h-0'
              }`}
              style={{
                top: '0',
                bottom: '0',
              }}
            >
              {/* Scroll-based progress line */}
              <div
                className={`absolute top-0 left-0 w-full bg-gradient-to-b from-green-400 to-green-600 transition-all duration-300 ease-out ${
                  visibleSections.has('how-it-works')
                    ? 'opacity-100'
                    : 'opacity-0'
                }`}
                style={{
                  height: `${scrollProgress}%`,
                }}
              />
            </div>

            {/* Timeline Steps */}
            <div className="relative space-y-12">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isLeft = index % 2 === 0;
                const isActive = activeSteps.has(index);
                const isVisible = visibleSections.has('how-it-works');

                return (
                  <div
                    key={step.id}
                    ref={(el) => {
                      stepRefs.current[index] = el;
                    }}
                    className={`relative flex items-center ${
                      isLeft ? 'justify-start' : 'justify-end'
                    } transition-all duration-1000 ${
                      isVisible
                        ? 'opacity-100 translate-x-0'
                        : isLeft
                          ? 'opacity-0 -translate-x-20'
                          : 'opacity-0 translate-x-20'
                    }`}
                    style={{
                      transitionDelay: isVisible ? `${index * 200}ms` : '0ms',
                      visibility: isVisible ? 'visible' : 'hidden',
                    }}
                  >
                    {/* Step Content */}
                    <div
                      className={`w-full sm:w-5/12 ${
                        isLeft ? 'pr-0 sm:pr-8 text-left sm:text-right' : 'pl-0 sm:pl-8 text-left'
                      } transition-all duration-500 ${
                        isActive ? 'scale-105' : 'scale-100'
                      }`}
                    >
                      <div
                        className={`bg-gradient-to-br from-gray-900/30 to-transparent rounded-lg border p-4 sm:p-6 transition-all duration-500 ${
                          isActive
                            ? 'border-green-400/50 shadow-lg shadow-green-400/20'
                            : 'border-gray-800/50'
                        }`}
                      >
                        <div
                          className={`flex items-center mb-3 ${
                            isLeft ? 'justify-start sm:justify-end' : 'justify-start'
                          }`}
                        >
                          <div
                            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                              isActive
                                ? 'bg-green-400/20 border-green-400 scale-110'
                                : 'bg-gray-800/50 border-gray-700'
                            }`}
                          >
                            <Icon
                              className={`w-5 h-5 sm:w-6 sm:h-6 transition-colors duration-500 ${
                                isActive ? 'text-green-400' : 'text-gray-400'
                              }`}
                            />
                          </div>
                        </div>
                        <h3
                          className={`font-megant text-lg sm:text-xl font-bold mb-2 transition-colors duration-500 ${
                            isActive ? 'text-white' : 'text-gray-300'
                          }`}
                        >
                          {step.title}
                        </h3>
                        <p className="font-montserrat text-gray-400 text-xs sm:text-sm leading-relaxed">{step.description}</p>
                      </div>
                    </div>

                    {/* Central Timeline Dot */}
                    <div
                      className={`absolute left-1/2 transform -translate-x-1/2 w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 transition-all duration-500 z-10 ${
                        isActive
                          ? 'bg-green-400 border-green-400 scale-125 shadow-lg shadow-green-400/50'
                          : 'bg-black border-gray-700 scale-100'
                      }`}
                    >
                      {isActive && (
                        <CheckCircle2 className="w-full h-full text-green-400" />
                      )}
                    </div>

                    {/* Step Number */}
                    <div
                      className={`absolute left-1/2 transform -translate-x-1/2 -bottom-6 sm:-bottom-8 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all duration-500 ${
                        isActive
                          ? 'bg-green-400 text-black scale-110'
                          : 'bg-gray-800 text-gray-400 scale-100'
                      }`}
                    >
                      {step.id}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile Timeline - Vertical Stack */}
          <div className="md:hidden relative">
            <div className="relative space-y-6">
              {/* Vertical connecting line */}
              <div
                className={`absolute left-6 top-0 bottom-0 w-0.5 bg-gray-800 transition-all duration-2000 ${
                  visibleSections.has('how-it-works')
                    ? 'opacity-100'
                    : 'opacity-0'
                }`}
              >
                {/* Progress line */}
                <div
                  className={`absolute top-0 left-0 w-full bg-gradient-to-b from-green-400 to-green-600 transition-all duration-300 ease-out ${
                    visibleSections.has('how-it-works')
                      ? 'opacity-100'
                      : 'opacity-0'
                  }`}
                  style={{
                    height: `${scrollProgress}%`,
                  }}
                />
              </div>

              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = activeSteps.has(index);
                const isVisible = visibleSections.has('how-it-works');

                return (
                  <div
                    key={step.id}
                    className={`relative pl-14 transition-all duration-1000 ${
                      isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                    }`}
                    style={{
                      transitionDelay: isVisible ? `${index * 150}ms` : '0ms',
                    }}
                  >
                    {/* Timeline dot */}
                    <div
                      className={`absolute left-4 top-0 w-5 h-5 rounded-full border-2 transition-all duration-500 z-10 flex items-center justify-center ${
                        isActive
                          ? 'bg-green-400 border-green-400 scale-125 shadow-lg shadow-green-400/50'
                          : 'bg-black border-gray-700 scale-100'
                      }`}
                    >
                      {isActive && (
                        <CheckCircle2 className="w-full h-full text-green-400" />
                      )}
                    </div>

                    {/* Step number badge */}
                    <div
                      className={`absolute left-2 top-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-500 z-20 ${
                        isActive
                          ? 'bg-green-400 text-black scale-110'
                          : 'bg-gray-800 text-gray-400 scale-100'
                      }`}
                    >
                      {step.id}
                    </div>

                    {/* Step content card */}
                    <div
                      className={`bg-gradient-to-br from-gray-900/30 to-transparent rounded-lg border p-4 transition-all duration-500 ${
                        isActive
                          ? 'border-green-400/50 shadow-lg shadow-green-400/20 scale-[1.02]'
                          : 'border-gray-800/50 scale-100'
                      }`}
                    >
                      <div className="flex items-center mb-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                            isActive
                              ? 'bg-green-400/20 border-green-400'
                              : 'bg-gray-800/50 border-gray-700'
                          }`}
                        >
                          <Icon
                            className={`w-5 h-5 transition-colors duration-500 ${
                              isActive ? 'text-green-400' : 'text-gray-400'
                            }`}
                          />
                        </div>
                      </div>
                      <h3
                        className={`font-megant text-base font-bold mb-2 transition-colors duration-500 ${
                          isActive ? 'text-white' : 'text-gray-300'
                        }`}
                      >
                        {step.title}
                      </h3>
                      <p className="font-montserrat text-gray-400 text-xs leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Section Divider */}
      <div className="relative z-10 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div
            className={`h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent transition-all duration-1000 ${
              visibleSections.has('how-it-works')
                ? 'opacity-100 scale-x-100'
                : 'opacity-0 scale-x-0'
            }`}
          />
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;

